import {AppState, Linking, NativeEventSubscription} from 'react-native';
import {
  PERMISSIONS,
  PermissionStatus,
  RESULTS,
  check,
  request,
  requestNotifications,
} from 'react-native-permissions';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {AppStateStatus} from '@buncha/home/types';
import Geolocation from 'react-native-geolocation-service';
import {getOs} from '../utils/appInfo';
import {useGetPermissionText} from './text';

export function useAppState() {
  const [appState, setAppState] = useState(AppState.currentState);
  const listener = useRef<NativeEventSubscription | null>(null);
  useEffect(() => {
    listener.current = AppState.addEventListener('change', (nextState: any) => {
      setAppState(nextState);
    });
    return () => {
      if (!listener) return;
      listener.current?.remove();
    };
  }, []);
  return appState;
}

export const useCheckAppState = () => {
  const appState = useAppState();
  const checkAppState = (stateToCheck: AppStateStatus): boolean => {
    return stateToCheck === appState;
  };
  return {checkAppState};
};

const initialPermissionState: {[key: string]: PermissionStatus | undefined} = {
  cameraPermission: undefined,
  notificationPermission: undefined,
  locationPermission: undefined,
};

interface PermissionState {
  cameraPermission: PermissionStatus | undefined;
  cameraPermissionStatus: string;
  requestCameraPermissions: () => Promise<void>;
  notificationPermission: PermissionStatus | undefined;
  notificationPermissionStatus: string;
  requestNotificationPermissions: () => Promise<void>;
  locationPermission: PermissionStatus | undefined;
  locationPermissionStatus: string;
  requestLocationPermission: () => Promise<void>;
}

const possibleValues = ['blocked', 'denied'];

export function usePermissionState(): PermissionState {
  const isAndroid = getOs() === 'android';
  const {getPermissionText} = useGetPermissionText();

  const [permissions, setPermissions] = useState(initialPermissionState);

  const {cameraPermission, locationPermission, notificationPermission} =
    permissions;

  const checkCameraPermission = useCallback(async () => {
    const res = await check(
      isAndroid ? PERMISSIONS.ANDROID.CAMERA : PERMISSIONS.IOS.CAMERA,
    );
    setPermissions(state => ({...state, cameraPermission: res}));
    return res;
  }, [isAndroid]);

  const requestCameraPermissions = useCallback(async () => {
    if ((await checkCameraPermission()) === RESULTS.GRANTED) return;
    let permission: PermissionStatus;
    const permissionsToRequest = isAndroid
      ? PERMISSIONS.ANDROID.CAMERA
      : PERMISSIONS.IOS.CAMERA;
    switch (cameraPermission) {
      case RESULTS.BLOCKED:
        Linking.openSettings();
        break;
      case RESULTS.DENIED:
        permission = await request(permissionsToRequest);
        if (permission === RESULTS.BLOCKED && isAndroid) Linking.openSettings();
        setPermissions(state => ({...state, cameraPermission: permission}));
        break;
      case RESULTS.GRANTED:
      default:
        break;
    }
  }, [cameraPermission, checkCameraPermission, isAndroid]);

  const cameraPermissionStatus = useMemo(() => {
    return getPermissionText(cameraPermission);
  }, [cameraPermission]);

  const checkNotificationPermission = async () => {
    const res = await requestNotifications(['alert', 'sound']);
    setPermissions(state => ({...state, notificationPermission: res.status}));
    return res.status;
  };

  const notificationPermissionStatus = useMemo(() => {
    return getPermissionText(notificationPermission);
  }, [notificationPermission]);

  const requestNotificationPermissions = useCallback(async () => {
    if ((await checkNotificationPermission()) === RESULTS.GRANTED) return;
    let permission: PermissionStatus;
    switch (notificationPermission) {
      case RESULTS.BLOCKED:
        Linking.openSettings();
        break;
      case RESULTS.DENIED:
        permission = await request(PERMISSIONS.ANDROID.POST_NOTIFICATIONS);
        if (permission === RESULTS.BLOCKED && isAndroid) Linking.openSettings();
        setPermissions(state => ({
          ...state,
          notificationPermission: permission,
        }));
        break;
      case RESULTS.GRANTED:
      default:
        break;
    }
  }, [isAndroid, notificationPermission]);

  const checkLocationPermission = useCallback(async () => {
    const res = await check(
      isAndroid
        ? PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION
        : PERMISSIONS.IOS.LOCATION_WHEN_IN_USE,
    );
    const response = isAndroid
      ? res &&
        !possibleValues.includes(res) &&
        (await new Promise(resolve => {
          Geolocation.getCurrentPosition(
            _ => resolve(true),
            _ => resolve(false),
          );
        }))
      : true;
    const finalResponse = response ? res : RESULTS.DENIED;
    setPermissions(state => ({
      ...state,
      locationPermission: finalResponse,
    }));
    return finalResponse;
  }, [isAndroid]);

  const locationPermissionStatus = useMemo(() => {
    return getPermissionText(locationPermission);
  }, [locationPermission]);

  const requestLocationPermission = useCallback(async () => {
    const response = await checkLocationPermission();
    if (response === RESULTS.GRANTED) return;
    let permission: PermissionStatus;
    switch (locationPermission) {
      case RESULTS.BLOCKED:
        Linking.openSettings();
        break;
      case RESULTS.DENIED:
      case RESULTS.UNAVAILABLE:
        permission = await request(
          isAndroid
            ? PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION
            : PERMISSIONS.IOS.LOCATION_WHEN_IN_USE,
        );
        if (permission === RESULTS.BLOCKED && isAndroid) Linking.openSettings();
        setPermissions(state => ({
          ...state,
          locationPermission:
            response && response !== RESULTS.DENIED
              ? permission
              : RESULTS.DENIED,
        }));
        break;
      case RESULTS.GRANTED:
      default:
        break;
    }
  }, [checkLocationPermission, isAndroid, locationPermission]);

  const checkPermissionsState = () => {
    checkCameraPermission();
    checkNotificationPermission();
    checkLocationPermission();
  };

  useEffect(() => {
    checkPermissionsState();
    AppState.addEventListener('change', state => {
      if (state === 'active' && !isAndroid) checkPermissionsState();
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    cameraPermission,
    cameraPermissionStatus,
    requestCameraPermissions,
    notificationPermission,
    notificationPermissionStatus,
    requestNotificationPermissions,
    locationPermission,
    locationPermissionStatus,
    requestLocationPermission,
  };
}
